import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MAX_EXTRA_PHOTOS_FE } from "@/lib/galleryUtils";

/**
 * Photo picker shown when merging multi-photo scans would exceed the 5-extras
 * cap. Users choose which candidate photos to keep as extras; the primary's
 * own extras are preserved outside this dialog. Owner state stays in parent
 * so opening + closing the dialog + confirming the merge remain adjacent to
 * `bulkMerge`. The dialog is otherwise fully controlled by props.
 *
 * Props (all owned by parent):
 *  - open, onOpenChange
 *  - loading                (fetching candidate photos)
 *  - primary                { thumb, name, primaryExtrasCount }
 *  - candidates             [{ key, scan_id, photo_id, thumb, sourceLabel }]
 *  - selection              ordered array of candidate keys ≤ effectiveCap
 *  - onToggleCandidate(key)
 *  - onCancel               closes the dialog
 *  - onConfirm(selectedCandidates)  fires bulkMerge with the ordered picks
 *  - merging                true while the /plants/merge call is in-flight
 */
export const MergePickerDialog = ({
  open, onOpenChange,
  loading,
  primary,
  candidates,
  selection,
  onToggleCandidate,
  onCancel,
  onConfirm,
  merging,
}) => {
  const { t } = useTranslation();
  const primaryExtras = primary?.primaryExtrasCount || 0;
  const effectiveCap = MAX_EXTRA_PHOTOS_FE - primaryExtras;

  const confirm = () => {
    const chosen = candidates
      .filter((c) => selection.includes(c.key))
      // Preserve the user's click order (matches `selection` order).
      .sort((a, b) => selection.indexOf(a.key) - selection.indexOf(b.key))
      .map((c) => ({ scan_id: c.scan_id, photo_id: c.photo_id }));
    onConfirm(chosen);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="merge-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="merge-picker-title">
            {t("gallery.mergePickerTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("gallery.mergePickerDesc", {
              remaining: effectiveCap,
              total: candidates.length,
            })}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-leaf-mist py-8 text-center" data-testid="merge-picker-loading">
            {t("gallery.mergePickerLoading")}
          </p>
        ) : (
          <>
            {primary && (
              <div className="rounded-xl bg-parchment/60 border border-[#E8E5DF] px-3 py-2 flex items-center gap-3">
                <img src={primary.thumb} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist">
                    {t("gallery.mergePickerPrimaryLabel")}
                  </p>
                  <p className="text-sm text-leaf-ink truncate font-medium">{primary.name}</p>
                  <p className="text-[11px] text-leaf-mist">
                    {t("gallery.mergePickerPrimaryExtras", { n: primaryExtras })}
                  </p>
                </div>
              </div>
            )}
            <div
              data-testid="merge-picker-counter"
              className="text-xs text-leaf-mist"
              aria-live="polite"
            >
              {t("gallery.mergePickerSelected", {
                n: selection.length,
                max: effectiveCap,
              })}
            </div>
            <div className="grid grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto pr-1">
              {candidates.map((c) => {
                const isChecked = selection.includes(c.key);
                const capReached = selection.length >= effectiveCap;
                const disabled = !isChecked && capReached;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => onToggleCandidate(c.key)}
                    disabled={disabled}
                    aria-pressed={isChecked}
                    aria-label={c.sourceLabel}
                    data-testid={`merge-picker-candidate-${c.key}`}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition disabled:opacity-40 ${
                      isChecked ? "border-leaf shadow-botanical" : "border-transparent hover:border-leaf/40"
                    }`}
                  >
                    <img src={c.thumb} alt={c.sourceLabel} className="w-full h-full object-cover" />
                    {isChecked && (
                      <span className="absolute top-1 right-1 bg-leaf text-white rounded-full w-6 h-6 flex items-center justify-center">
                        <Check className="w-4 h-4" strokeWidth={2} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            data-testid="merge-picker-cancel"
            className="text-sm px-4 py-2 rounded-full bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            data-testid="merge-picker-confirm"
            disabled={merging || loading || selection.length === 0}
            onClick={confirm}
            className="text-sm px-4 py-2 rounded-full bg-leaf hover:bg-leaf-deep text-white disabled:opacity-50"
          >
            {merging ? t("gallery.merging") : t("gallery.mergeConfirm")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergePickerDialog;
